/**
 * USFWS NWI wetland polygons as generation-layout exclusion zones.
 *
 * Same contract as `femaFloodExclusions`: fetch once at click time, clip to
 * the parcel, snapshot into `GenerationLayoutConfiguration.exclusions`, fail
 * closed on malformed geometry so absence of screening is always explicit.
 *
 * NWI host + `Wetlands.*` joined-field prefix follow the hard-won PRR-3
 * conventions in `usfwsHabitat.ts`.
 */
import polygonClipping from 'polygon-clipping';

import type {
  GenerationLayoutExclusionSet,
  GenerationLayoutExclusionZone,
} from '../../types/generationImpact';
import { throwIfAborted } from '../../lib/abortSignal';
import { queryArcgisRest } from '../arcgis/queryArcgisRest';
import { arcgisRingsToMultiPolygon } from './femaFlood';
import { refdataBbox, tryRefdata, isPrivateRefdataAcceptanceDataset } from '../refdata/refdataClient';
import type { NwiMappingCoverage, RefdataGeometry } from '../../types/refdata';
import { nwiMappingCoverage } from './nwiMappingCoverage';

type Position = [number, number];
type MultiPolygon = polygonClipping.MultiPolygon;

const NWI_QUERY_URL = 'https://fwspublicservices.wim.usgs.gov/wetlandsmapservice/rest/services/Wetlands/MapServer/0/query';
const NWI_PROVIDER_ID = 'usfws-nwi';
const NWI_HOME_URL = 'https://www.fws.gov/program/national-wetlands-inventory';
const COORDINATE_DECIMALS = 7;
const AREA_EPSILON = 1e-12;
const MAX_FEATURES = 200;

export interface FetchNwiWetlandExclusionZonesOptions {
  signal?: AbortSignal;
  /** Test seam: replaces the live ArcGIS query. */
  queryRest?: typeof queryArcgisRest;
  /** Test seam: replaces the wall clock recorded in provenance. */
  now?: () => Date;
}

/** Result mirrors GenerationLayoutExclusionSet minus FEMA bookkeeping. */
export type NwiWetlandExclusionResult =
  | { status: 'applied'; zones: GenerationLayoutExclusionZone[]; retrievedAt: string; coverage: NwiMappingCoverage }
  | { status: 'partial'; zones: GenerationLayoutExclusionZone[]; reason: string; retrievedAt: string; coverage: NwiMappingCoverage }
  | { status: 'unavailable'; reason: string; retrievedAt: string };

function roundCoordinate(value: number): number {
  const scale = 10 ** COORDINATE_DECIMALS;
  return Math.round(value * scale) / scale;
}

function closeParcelRing(boundary: ReadonlyArray<{ lat: number; lng: number }>): Position[] | null {
  const ring: Position[] = boundary
    .filter(point => Number.isFinite(point.lat) && Number.isFinite(point.lng))
    .map(point => [point.lng, point.lat]);
  if (ring.length < 3) return null;
  const first = ring[0]!;
  const last = ring.at(-1)!;
  if (first[0] !== last[0] || first[1] !== last[1]) ring.push([...first]);
  return ring;
}

function ringSignedArea(ring: ReadonlyArray<readonly number[]>): number {
  let twiceArea = 0;
  for (let index = 0; index + 1 < ring.length; index += 1) {
    const current = ring[index]!;
    const next = ring[index + 1]!;
    twiceArea += current[0]! * next[1]! - next[0]! * current[1]!;
  }
  return twiceArea / 2;
}

function multiPolygonToLatLngRings(geometry: MultiPolygon): Array<Array<{ lat: number; lng: number }>> {
  return geometry
    .map(polygon => polygon[0])
    .filter((outer): outer is polygonClipping.Ring => outer != null && outer.length >= 4)
    .map(outer => ({
      absArea: Math.abs(ringSignedArea(outer)),
      ring: outer.map(([lng, lat]) => ({
        lat: roundCoordinate(lat),
        lng: roundCoordinate(lng),
      })),
    }))
    .filter(entry => entry.absArea > AREA_EPSILON)
    .sort((left, right) => right.absArea - left.absArea
      || left.ring[0]!.lat - right.ring[0]!.lat
      || left.ring[0]!.lng - right.ring[0]!.lng)
    .map(entry => entry.ring);
}

function wetlandTypeOf(attributes: Record<string, unknown> | undefined): string {
  const raw = attributes?.['Wetlands.WETLAND_TYPE'] ?? attributes?.WETLAND_TYPE;
  const label = typeof raw === 'string' ? raw.trim() : '';
  return label || 'Unclassified wetland';
}

/** GeoJSON polygonal geometry → polygon-clipping MultiPolygon coords.
 *  Null for non-polygonal geometry (fail-closed upstream). */
function geojsonToMultiPolygon(geom: RefdataGeometry): MultiPolygon | null {
  if (geom.type === 'Polygon') return [geom.coordinates] as MultiPolygon;
  if (geom.type === 'MultiPolygon') return geom.coordinates as MultiPolygon;
  return null;
}

function slug(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

/**
 * Fetch NWI wetland exclusion zones for one parcel polygon, grouped per
 * wetland type and canonically ordered.
 */
export async function fetchNwiWetlandExclusionZones(
  boundary: ReadonlyArray<{ lat: number; lng: number }>,
  options: FetchNwiWetlandExclusionZonesOptions = {},
): Promise<NwiWetlandExclusionResult> {
  const retrievedAt = (options.now?.() ?? new Date()).toISOString();
  throwIfAborted(options.signal);

  const parcelRing = closeParcelRing(boundary);
  if (!parcelRing || parcelRing.length < 4) {
    return { status: 'unavailable', reason: 'parcel geometry is malformed', retrievedAt };
  }

  const lats = parcelRing.map(([, lat]) => lat);
  const lngs = parcelRing.map(([lng]) => lng);
  const bbox = {
    minLat: Math.min(...lats),
    minLng: Math.min(...lngs),
    maxLat: Math.max(...lats),
    maxLng: Math.max(...lngs),
  };

  // RD-1 (C2) — local-first: the full-geometry (unsimplified) NWI snapshot
  // serves the same envelope query from GCS. The fail-closed contract is
  // preserved: malformed snapshot geometry → 'unavailable', never a silent
  // partial screen. A refdata miss/error falls through to the live MapServer.
  interface TypedWetland { type: string; geometry: MultiPolygon }
  let wetlands: TypedWetland[] | null = null;
  const local = await tryRefdata('nwi-wetlands', () => refdataBbox('nwi-wetlands', bbox, MAX_FEATURES));
  let coverage: NwiMappingCoverage = await nwiMappingCoverage(local, { point: boundary[0]!, polygon: boundary });
  if (local?.truncated !== false) coverage = { status: 'unknown', reason: local ? 'NWI snapshot feature query was truncated or lacks a completion receipt.' : 'Live NWI query has no matching mapping-coverage receipt.' };
  if (local) {
    const collected: TypedWetland[] = [];
    let malformed = false;
    for (const feature of local.features) {
      const geometry = geojsonToMultiPolygon(feature.geom);
      if (!geometry) {
        malformed = true;
        continue;
      }
      collected.push({ type: wetlandTypeOf(feature.props as Record<string, unknown>), geometry });
    }
    if (malformed) coverage = { status: 'unknown', reason: 'NWI snapshot returned wetland features with malformed geometry' };
    wetlands = collected;
  }
  throwIfAborted(options.signal);

  if (!wetlands) {
    if (isPrivateRefdataAcceptanceDataset('nwi-wetlands')) throw new Error('Private NWI acceptance exclusions unavailable; live supplementation is withheld');
    const envelope = `${bbox.minLng},${bbox.minLat},${bbox.maxLng},${bbox.maxLat}`;
    const url = `${NWI_QUERY_URL}?where=1%3D1&geometry=${encodeURIComponent(envelope)}`
      + '&geometryType=esriGeometryEnvelope&inSR=4326&outSR=4326'
      + '&spatialRel=esriSpatialRelIntersects'
      + '&outFields=Wetlands.WETLAND_TYPE&returnGeometry=true&geometryPrecision=7'
      + `&resultRecordCount=${MAX_FEATURES}&f=json`;

    const query = options.queryRest ?? queryArcgisRest;
    let features: Array<{ attributes?: Record<string, unknown>; geometry?: { rings?: number[][][] } }>;
    try {
      const response = await query(url, { label: 'NWI Wetlands exclusions', signal: options.signal });
      if (!Array.isArray(response?.features)) throw new Error('malformed features payload');
      features = response.features as typeof features;
      if (response.exceededTransferLimit === true || features.length >= MAX_FEATURES) coverage = { status: 'unknown', reason: 'NWI live feature query exceeded its result limit; screening is incomplete.' };
    } catch (error) {
      if (options.signal?.aborted) throw error;
      return {
        status: 'unavailable',
        reason: `NWI wetlands query failed: ${(error as Error).message}`,
        retrievedAt,
      };
    }
    throwIfAborted(options.signal);

    wetlands = [];
    for (const feature of features) {
      const rawRings = feature.geometry?.rings;
      const featureGeometry = rawRings ? arcgisRingsToMultiPolygon(rawRings) : null;
      if (!featureGeometry) {
        coverage = { status: 'unknown', reason: 'NWI returned wetland features with malformed geometry' };
        continue;
      }
      wetlands.push({ type: wetlandTypeOf(feature.attributes), geometry: featureGeometry });
    }
  }

  const parcelGeometry = [[parcelRing]] as MultiPolygon;
  const byType = new Map<string, MultiPolygon[]>();
  for (const wetland of wetlands) {
    const group = byType.get(wetland.type) ?? [];
    group.push(wetland.geometry);
    byType.set(wetland.type, group);
  }

  const zones: GenerationLayoutExclusionZone[] = [];
  for (const [type, geometries] of byType) {
    try {
      let merged = geometries[0]!;
      for (let index = 1; index < geometries.length; index += 1) {
        merged = polygonClipping.union(merged, geometries[index]!) as MultiPolygon;
      }
      const clipped = polygonClipping.intersection(merged, parcelGeometry) as MultiPolygon;
      const polygons = multiPolygonToLatLngRings(clipped);
      if (polygons.length === 0) continue;
      zones.push({
        id: `nwi-${slug(type)}`,
        label: `NWI wetland — ${type}`,
        source: 'usfws-nwi',
        kind: 'wetland',
        polygons,
        provenance: { providerId: NWI_PROVIDER_ID, retrievedAt, url: NWI_HOME_URL },
      });
    } catch {
      coverage = { status: 'unknown', reason: 'Some NWI wetland geometry could not be clipped to the parcel' };
    }
  }

  zones.sort((left, right) => left.id.localeCompare(right.id));
  if (coverage.status !== 'complete') return zones.length > 0
    ? { status: 'partial', zones, retrievedAt, coverage, reason: coverage.reason }
    : { status: 'unavailable', retrievedAt, reason: coverage.reason };
  return { status: 'applied', zones, retrievedAt, coverage };
}
